import React from "react";
import cs from "classnames";
import styles from "./styles.module.scss";
import { CloseBigIcon } from "./icons";
import { AsoReportProps } from "./types";

const DEFAULT_MAX_COMPETITORS = 4;

/** Anchor id of a section — deep links jump straight to it. */
const anchorId = (id: string) => `aso-${id}`;

const AppIcon: React.FC<{
  icon?: string | null;
  title: string;
  className: string;
}> = ({ icon, title, className }) =>
  icon ? (
    <img className={className} src={icon} alt="" />
  ) : (
    <span className={cs(className, styles.iconFallback)}>
      {title.slice(0, 1).toUpperCase()}
    </span>
  );

/**
 * ASO Report card (Figma 4276:4453 "App Insights - Modals - ASO Report"):
 * heading with a close button, then a stack of outlined cards — the app with
 * the competitors it is measured against first, the report sections after.
 *
 * Pure presentational and section-agnostic: every section is passed in as
 * `{ id, title, content }`, so sections can land one by one as their data
 * sources are built. A section without content renders its empty state.
 */
export const AsoReport: React.FC<AsoReportProps> = ({
  title = "ASO Report",
  appName,
  appIcon,
  appDeveloper,
  countryLabel,
  onClose,
  closeLabel = "Close",
  competitors = [],
  maxCompetitors = DEFAULT_MAX_COMPETITORS,
  sections,
  emptyText = "No data yet",
  className,
}) => (
  <div className={cs(styles.root, className)}>
    <div className={styles.head}>
      <h2 className={styles.title}>{title}</h2>
      {onClose && (
        <button
          type="button"
          className={styles.close}
          onClick={onClose}
          aria-label={closeLabel}
          title={closeLabel}
        >
          <CloseBigIcon />
        </button>
      )}
    </div>

    {/* ---- the app and its competitors ---- */}
    <header className={styles.card}>
      <div className={styles.app}>
        <AppIcon icon={appIcon} title={appName} className={styles.appIcon} />
        <div className={styles.appText}>
          <span className={styles.appName}>{appName}</span>
          {(appDeveloper || countryLabel) && (
            <span className={styles.appMeta}>
              {appDeveloper && <span>{appDeveloper}</span>}
              {appDeveloper && countryLabel && (
                <span className={styles.appMetaDot}>•</span>
              )}
              {countryLabel && <span>{countryLabel}</span>}
            </span>
          )}
        </div>
      </div>

      {competitors.length > 0 && (
        <div className={styles.competitors}>
          <span className={styles.competitorsLabel}>
            Competitors ({competitors.length}/{maxCompetitors})
          </span>
          <div className={styles.competitorsList}>
            {competitors.map((competitor) => (
              <span className={styles.competitor} key={competitor.id}>
                <AppIcon
                  icon={competitor.icon}
                  title={competitor.title}
                  className={styles.competitorIcon}
                />
                <span className={styles.competitorTitle}>
                  {competitor.title}
                </span>
              </span>
            ))}
          </div>
        </div>
      )}
    </header>

    {/* ---- sections ---- */}
    {sections.map((section) => (
      <section
        key={section.id}
        id={anchorId(section.id)}
        className={styles.card}
      >
        <header className={styles.sectionHead}>
          <h3 className={styles.sectionTitle}>{section.title}</h3>
          {section.badge != null && (
            <span className={styles.badge}>{section.badge}</span>
          )}
        </header>

        {section.loading ? (
          <div className={styles.skeleton} />
        ) : (
          (section.content ?? (
            <p className={styles.empty}>{section.emptyText ?? emptyText}</p>
          ))
        )}
      </section>
    ))}
  </div>
);

export default AsoReport;
